"use client";

import { IDashboardUserBadge } from "@/api";
import {
  Box,
  Typography,
  Grid,
  Paper,
  IconButton,
  Pagination,
  PaginationItem,
  CircularProgress,
  Link,
  Button,
  CardContent,
  Card,
  CardMedia,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { apiRemote } from "@/api/wp-api";
import { useEffect, useRef, useState } from "react";
import {
  ArrowBackIos,
  ArrowForward,
  ArrowForwardIos,
} from "@mui/icons-material";
import { useMyBadgeContext } from "./Layout";

export interface IUserBadgeExtend extends IDashboardUserBadge {}

export default function MyBadge() {
  const [badges, setBadges] = useState<IDashboardUserBadge["data"]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false); // Trạng thái loading
  const [pageSize, setPageSize] = useState(4);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const newSize = isMobile ? 1 : 4;
    if (pageSize !== newSize) {
        setPageSize(newSize);
        return; // Tránh gọi fetch khi pageSize vừa thay đổi
    }
    const fetchBadges = async () => {
      setLoading(true); // Bắt đầu tải dữ liệu
      try {
        const res = await apiRemote.getDashboardUserBadge(
          currentPage.toString(),
          pageSize.toString()
        );
        setBadges(res.data);
        setTotalPages(Number(res.total_pages));
        setCount(Number(res.count));
      } catch (error) {
        console.error("Error fetching badges:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBadges();
  }, [currentPage, pageSize, isMobile]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        bgcolor: "white",
        borderRadius: 2,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        flex: 1,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: "rgb(15, 105, 185)",
          fontWeight: 500,
          mb: 3,
        }}
      >
        My Badges
      </Typography>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 3,
          }}
        >
          <CircularProgress />
        </Box>
      ) : count === 0 ? (
        <Typography
          sx={{
            color: "rgb(102, 102, 102)",
            fontSize: "1rem",
            textAlign: "center",
          }}
        >
          No data available
        </Typography>
      ) : (
        <>
          <Box
            sx={{
              position: "relative",
              mb: 3,
            }}
          >
            <Grid
              container
              spacing={3}
              sx={{
                flexWrap: "nowrap",
                overflowX: "hidden",
                mb: 2,
              }}
            >
              {Array.isArray(badges) &&
                badges.slice(0, pageSize).map((badge) => (
                  <Grid item xs={12} sm={6} md={3} key={badge.badgeId}>
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        bgcolor: "rgb(247, 250, 255)",
                        border: "1px solid rgb(237, 242, 247)",
                        boxShadow: "none",
                        borderRadius: 2,
                      }}
                    >
                      <CardMedia
                        component="div"
                        sx={{
                          pt: "56.25%",
                          position: "relative",
                          bgcolor: "white",
                        }}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: "100%",
                            height: "100%",
                          }}
                        >
                          <img
                            src={badge.badgeIamge}
                            alt={badge.badgeName}
                            width="100%"
                            height="100%"
                            style={{
                              objectFit: "cover",
                            }}
                          />
                        </Box>
                      </CardMedia>
                      <CardContent
                        sx={{
                          flexGrow: 1,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography
                          gutterBottom
                          variant="h6"
                          component="h2"
                          sx={{ fontSize: "1rem", mb: 2, textAlign: "center" }}
                        >
                          {badge.badgeName}
                        </Typography>
                        <Button
                          href={badge.badgeView}
                          target="_blank"
                          rel="noopener noreferrer"
                          fullWidth
                          variant="contained"
                          sx={{
                            bgcolor: "rgb(255, 90, 31)",
                            "&:hover": {
                              bgcolor: "rgb(230, 81, 28)",
                            },
                          }}
                        >
                          Go
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
            </Grid>

            <IconButton
              onClick={handlePrevPage} // Chuyển trang về trước
              sx={{
                position: "absolute",
                left: -16,
                top: "50%",
                transform: "translateY(-50%)",
                bgcolor: "white",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                "&:hover": {
                  bgcolor: "rgb(247, 250, 255)",
                },
              }}
            >
              <ArrowBackIos />
            </IconButton>

            <IconButton
              onClick={handleNextPage} // Chuyển trang tới tiếp theo
              sx={{
                position: "absolute",
                right: -16,
                top: "50%",
                transform: "translateY(-50%)",
                bgcolor: "white",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                "&:hover": {
                  bgcolor: "rgb(247, 250, 255)",
                },
              }}
            >
              <ArrowForwardIos />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              shape="rounded"
              renderItem={(item) => (
                <PaginationItem
                  {...item}
                  sx={{
                    "&.Mui-selected": {
                      bgcolor: "rgb(15, 105, 185)",
                      color: "white",
                      "&:hover": {
                        bgcolor: "rgb(13, 94, 166)",
                      },
                    },
                  }}
                />
              )}
            />
          </Box>
        </>
      )}
    </Box>
  );
}
